import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const INITIAL = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll reply shortly.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls = "bg-[#121212] border-axe-border rounded-xl focus-visible:ring-1 focus-visible:ring-axe-purple focus-visible:border-axe-purple text-white h-12 px-4";

  return (
    <div data-testid="contact-page" className="text-white">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
            Get in touch
          </div>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Contact <span className="text-axe-purple">AXE</span>
          </h1>
          {content?.contact_blurb && (
            <p className="mt-6 text-neutral-400 text-base sm:text-lg max-w-lg">
              {content.contact_blurb}
            </p>
          )}

          <div className="mt-10 space-y-4">
            {(content?.contact_emails || []).map((c) => (
              <a
                key={c.email}
                href={`mailto:${c.email}`}
                className="flex items-center gap-4 rounded-2xl border border-axe-border bg-axe-surface p-5 hover:border-axe-purple/60 transition-colors"
                data-testid={`contact-email-${c.email}`}
              >
                <div className="h-12 w-12 rounded-xl bg-axe-purple/15 border border-axe-purple/40 grid place-items-center">
                  <Mail size={18} className="text-axe-purple" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-axe-tangerine font-bold">
                    {c.label}
                  </div>
                  <div className="mt-1 font-display font-semibold text-base text-white">
                    {c.email}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        <form
          onSubmit={submit}
          className="bg-axe-surface border border-axe-border rounded-2xl p-6 md:p-8 space-y-5"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="contact-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="contact-email" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="Partnership, press, support…" data-testid="contact-subject" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Message</Label>
            <Textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 bg-[#121212] border-axe-border rounded-xl text-white p-4 min-h-[160px] focus-visible:ring-1 focus-visible:ring-axe-purple focus-visible:border-axe-purple" data-testid="contact-message" />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto rounded-full bg-axe-purple px-8 py-3.5 font-bold uppercase tracking-[0.22em] text-white hover:bg-axe-purple-hover transition-colors disabled:opacity-50 drop-shadow-[0_0_14px_rgba(176,38,255,0.45)]"
            data-testid="contact-submit"
          >
            {submitting ? "Sending…" : "Send Message"}
          </button>
        </form>
      </section>
    </div>
  );
}
